import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'
import {
  AnnouncementContextInterface,
  AnnouncementListResponseData,
  SendAnnouncementRequestData,
  UpdatedAnnouncementData,
} from './types'

export const AnnouncementContext =
  createContext<AnnouncementContextInterface | null>(null)

export const AnnouncementProvider = ({
  children,
}: {
  children?: ReactNode
}) => {
  const [announcements, setAnnouncements] =
    useState<AnnouncementListResponseData>([])

  const { fetch } = useAxios()

  const getListOfAnnouncements = useCallback(async () => {
    const { data } = await fetch({
      path: 'Announcements/GetListOfAllAnnouncements',
    })
    if (data) {
      setAnnouncements(data)
    }
  }, [])

  // default send to all patients
  const sendNewAnnouncement = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/AddNewAnnouncement`,
      methodType: 'POST',

      body: announcementRequestData,
    })
    getListOfAnnouncements()
    console.log('error', error)
    if (error) {
      throw new Error(`Error in sendNewAnnouncement.`)
    }
  }

  const sendAnnouncementToAllOfMyPatients = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/SendAnnouncementToAllOfMyPatients`,
      methodType: 'POST',
      body: announcementRequestData,
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in SendAnnouncementToAllOfMyPatients.`)
    }
  }

  const sendAnnouncementToProvidersPatients = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/SendAnnouncementToProvidersPatients`,
      methodType: 'POST',
      body: announcementRequestData,
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in sendAnnouncementToProvidersPatients.`)
    }
  }

  const sendAnnouncementToPeerGroupParticipants = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/SendAnnouncementToPeerGroupParticipants`,
      methodType: 'POST',
      body: announcementRequestData,
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in SendAnnouncementToPeerGroupParticipants.`)
    }
  }

  const scheduleAnnouncement = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/scheduleAnnouncement`,
      methodType: 'POST',

      body: announcementRequestData,
    })
    getListOfAnnouncements()
    console.log('error', error)
    if (error) {
      throw new Error(`Error in scheduleAnnouncement.`)
    }
  }

  const scheduleAnnouncementToAllOfMyPatients = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/ScheduleAnnouncementToAllOfMyPatients`,
      methodType: 'POST',
      body: announcementRequestData,
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in ScheduleAnnouncementToAllOfMyPatients.`)
    }
  }

  const scheduleAnnouncementToProvidersPatients = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/ScheduleAnnouncementToProvidersPatients`,
      methodType: 'POST',
      body: announcementRequestData,
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in ScheduleAnnouncementToProvidersPatients.`)
    }
  }

  const scheduleAnnouncementToPeerGroupParticipants = async (
    announcementRequestData: SendAnnouncementRequestData
  ) => {
    const { error } = await fetch({
      path: `Announcements/ScheduleAnnouncementToPeerGroupParticipants`,
      methodType: 'POST',
      body: announcementRequestData,
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in ScheduleAnnouncementToPeerGroupParticipants.`)
    }
  }

  const updateAnnouncement = async (
    updatedAnnouncementData: UpdatedAnnouncementData
  ) => {
    const { error } = await fetch({
      path: `Announcements/UpdateAnnouncement`,
      methodType: 'PUT',
      body: updatedAnnouncementData,
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in updateAnnouncement.`)
    }
  }

  const cancelScheduledAnnouncement = async (announcementId: number) => {
    const { error } = await fetch({
      path: `Announcements/CancelScheduledAnnouncement?announcementId=${announcementId}`,
      methodType: 'PUT',
    })
    getListOfAnnouncements()
    if (error) {
      console.log('error', error)
      throw new Error(`Error in cancelScheduledAnnouncement.`)
    }
  }

  useEffect(() => {
    getListOfAnnouncements()
  }, [])

  return (
    <AnnouncementContext.Provider
      value={{
        announcements,
        sendNewAnnouncement,
        sendAnnouncementToAllOfMyPatients,
        sendAnnouncementToProvidersPatients,
        sendAnnouncementToPeerGroupParticipants,
        getListOfAnnouncements,
        updateAnnouncement,
        scheduleAnnouncement,
        scheduleAnnouncementToAllOfMyPatients,
        scheduleAnnouncementToProvidersPatients,
        scheduleAnnouncementToPeerGroupParticipants,
        cancelScheduledAnnouncement,
      }}
    >
      {children}
    </AnnouncementContext.Provider>
  )
}

export const useAnnouncement = () =>
  useContext(AnnouncementContext) as AnnouncementContextInterface
